import { ReactNode, useState, useMemo, useId } from 'react';

import { useDropzone } from 'react-dropzone';
import classNames from 'classnames';
import { useTheme } from '@mui/material/styles';
import Button from '@mui/material/Button';

import { useImageConverter } from '../../services/hooks';
import { useIsDesktop } from '../../hooks';
import { Box } from '../../Box';
import { CircularProgress } from '../../CircularProgress';
import { FileInputValue } from '../FileInput';
import { Typography } from '../../Typography';
import { FileError, Label } from '../common';
import { Icon } from '../../Icon';

import { MultipleFileInputPreview } from './MultipleFileInputPreview';
import useStyles from './MultipleFileInput.styles';

const getFileKey = (file: FileInputValue): string => {
  if (typeof file.value === 'string') return file.value;
  return `${file.value.name}-${file.value.size}-${file.value.lastModified}`;
};

/**
 * Свойства компонента {@link MultipleFileInput}
 */
interface MultipleFileInputProps {
  /**
   * Атрибут ID для поля ввода.
   */
  id?: string;

  /**
   * Текущий список загруженных файлов.
   */
  value: FileInputValue[];

  /**
   * Функция обратного вызова, вызываемая при изменении списка файлов.
   */
  onChange: (value: FileInputValue[]) => void;

  /**
   * Функция обратного вызова, вызываемая при потере фокуса.
   */
  onBlur?: () => void;

  /**
   * Метка для компонента (обязательное поле).
   */
  label: string;

  /**
   * Иконка для метки.
   */
  labelIcon?: ReactNode;

  /**
   * Всплывающая подсказка для метки.
   */
  tooltip?: ReactNode;

  /**
   * Флаг, указывающий, является ли поле обязательным.
   */
  required?: boolean;

  /**
   * Флаг, указывающий, отключено ли поле.
   */
  disabled?: boolean;

  /**
   * Флаг, указывающий, происходит ли загрузка.
   */
  loading?: boolean;

  /**
   * Функция валидации загруженных файлов.
   */
  validate?: (
    value: unknown,
    allValues?: unknown,
  ) => string | undefined | Promise<string | undefined>;

  /**
   * Функция для кэширования файла.
   */
  cache?: (file: File) => Promise<{ file_id: string; expired_at: string }>;

  /**
   * Флаг, указывающий, есть ли ошибка в поле.
   */
  error?: boolean;

  /**
   * Вспомогательный текст для отображения ошибок или подсказок.
   */
  helperText?: string;

  /**
   * Описание для поля загрузки.
   */
  description?: string;

  /**
   * Текст для подсказки в поле загрузки изображения.
   */
  dropzoneText?: string;

  /**
   * Максимальное количество документов, доступное для подкрепления.
   */
  maxLength?: number;

  /**
   * Флаг авторизации.
   */
  withAuth?: boolean;

  /**
   * Показывать счётчик с порядковым номером у превью файлов.
   *
   * @defaultValue `true`
   */
  withCounter?: boolean;

  /**
   * Тестовый идентификатор для компонента.
   *
   * @defaultValue `'test-id-multiple-file-input'`
   */
  testId?: string;
}

/**
 * Компонент MultipleFileInput представляет собой элемент интерфейса для загрузки нескольких файлов.
 * Он поддерживает функциональность перетаскивания файлов и загрузку через кнопку.
 *
 * @public
 * @param id - Атрибут ID для поля ввода.
 * @param value - Текущий список загруженных файлов.
 * @param onChange - Функция обратного вызова, вызываемая при изменении списка файлов.
 * @param onBlur - Функция обратного вызова, вызываемая при потере фокуса.
 * @param label - Метка для компонента (обязательное поле).
 * @param labelIcon - Иконка для метки.
 * @param tooltip - Всплывающая подсказка для метки.
 * @param required - Флаг, указывающий, является ли поле обязательным.
 * @param disabled - Флаг, указывающий, отключено ли поле.
 * @param loading - Флаг, указывающий, происходит ли загрузка.
 * @param validate - Функция валидации загруженных файлов.
 * @param cache - Функция для кэширования файла.
 * @param error - Флаг, указывающий, есть ли ошибка в поле.
 * @param helperText - Вспомогательный текст для отображения ошибок или подсказок.
 * @param description - Описание для поля загрузки.
 * @param dropzoneText - Текст для подсказки в поле загрузки изображения.
 * @param maxLength - Максимальное количество документов, доступное для подкрепления.
 * @param withAuth - Флаг авторизации.
 * @param testId - Тестовый идентификатор для компонента.
 * @param withCounter - Показывать счётчик с порядковым номером у превью файлов.
 * @returns  Компонент MultipleFileInput.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link MultipleFileInputProps}
 */
export const MultipleFileInput = ({
  id: htmlId,
  value,
  onChange,
  onBlur = () => {},
  label,
  labelIcon,
  tooltip,
  required = false,
  disabled = false,
  loading = false,
  validate,
  cache,
  error = false,
  helperText = '',
  description = 'Загрузите изображение, PDF или DOCX файл',
  dropzoneText = 'Загрузить ещё',
  maxLength,
  withAuth = true,
  withCounter = true,
  testId = 'test-id-multiple-file-input',
  ...rest
}: MultipleFileInputProps) => {
  const { classes } = useStyles();
  const reactId = useId();
  const inputId = useMemo(() => {
    return htmlId ?? reactId;
  }, [htmlId, reactId]);
  const imageConverter = useImageConverter();
  const isDesktop = useIsDesktop();
  const theme = useTheme();

  const [isFileLoading, setIsFileLoading] = useState(false);
  const [dropError, setDropError] = useState('');

  const errorToDisplay = dropError ? dropError : helperText;

  const isLoading = loading || isFileLoading;
  const isDisabled = disabled || isLoading;

  /**
   * Обработчик события перетаскивания файлов.
   * При перетаскивании файлов они валидируются, кэшируются (если предоставлено соответствующее свойство),
   * и добавляются к текущему списку файлов.
   *
   * @param files - Массив загруженных файлов.
   */
  const { getRootProps, getInputProps } = useDropzone({
    disabled: isDisabled,
    onDrop: async (files) => {
      if (files && files.length) {
        if (cache) {
          setIsFileLoading(true);
          setDropError('');

          const errorText =
            (await validate?.(
              files.map((file) => ({ source: file })),
              {},
            )) || '';
          if (errorText) {
            setDropError(errorText);
            setIsFileLoading(false);
            return;
          }

          const cachedFiles = [];
          for await (const file of files) {
            try {
              const { file_id: cacheId, expired_at: cacheExpiredAt } =
                await cache(file);
              cachedFiles.push({
                value: JSON.stringify({
                  type: 'file_cache',
                  id: cacheId,
                  expiredAt: cacheExpiredAt,
                }),
                source: file,
              });
            } catch (e) {
              cachedFiles.push({ value: file, source: file });
            }
          }

          onChange([...value, ...cachedFiles].slice(0, maxLength));

          setIsFileLoading(false);
        } else {
          setIsFileLoading(() => true);
          const cachedFiles = files.map(async (file) => {
            const fileName = file.name.toLowerCase();
            const isHEICByExtension =
              fileName.endsWith('.heic') || fileName.endsWith('.heif');
            const isWrongFormat =
              isHEICByExtension ||
              [
                'image/heic',
                'image/heif',
                'image/heif-sequence',
                'image/heic-sequence',
                'image/tiff',
                'image/dng',
                'image/x-adobe-dng',
              ].includes(file.type);

            if (isWrongFormat) {
              const blob = await imageConverter.convert(file);
              const convertedFileName = file.name.replace(/\.[^.]+$/, '.jpeg');
              const blobFile = new File([blob], convertedFileName, {
                type: blob.type,
              });

              return { value: blobFile, source: blobFile };
            }

            return {
              value: file,
              source: file,
            };
          });

          Promise.all(cachedFiles).then((res) => {
            setIsFileLoading(() => false);
            return onChange([...value, ...res].slice(0, maxLength));
          });
        }
      } else {
        onChange([]);
      }

      onBlur();
    },
  });

  const isInputHasFiles = !!value?.length;

  const previews = useMemo(
    () =>
      value.map?.((file: FileInputValue, index: number) => {
        /**
         * Функция для удаления файла из списка.
         * Создает обработчик события, который удаляет файл по индексу из текущего списка файлов.
         *
         * @returns  Обработчик события удаления файла.
         */
        const removeDocument = () =>
          onChange([...value.slice(0, index), ...value.slice(index + 1)]);

        return (
          <MultipleFileInputPreview
            key={getFileKey(file)}
            file={file}
            index={index}
            removeDocument={removeDocument}
            disabled={disabled}
            withAuth={withAuth}
            withCounter={withCounter}
            testId={testId}
          />
        );
      }),
    [disabled, onChange, testId, value, withAuth, withCounter],
  );

  return (
    <Box className="aqa_multiple_file_input" testId={testId}>
      {label && (
        <Label
          label={label}
          required={required}
          tooltip={tooltip}
          icon={labelIcon}
          inputId={inputId}
        />
      )}
      <Box
        display="flex"
        alignItems="center"
        flexWrap="wrap"
        gap={isDesktop ? '24' : '8'}
        width="100%"
      >
        {isInputHasFiles && previews}
        {(!maxLength || value.length < maxLength) && (
          <Box flexGrow={1}>
            {isLoading ? (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                width={isInputHasFiles ? { xs: 92, md: 140 } : '100%'}
                height={{ xs: 92, md: 140 }}
                bgcolor="bg.light.primary"
                border={`${theme.tokens.border.s}px dashed`}
                borderColor="stroke.primary"
                radius="l"
              >
                <CircularProgress size={isInputHasFiles ? 30 : 50} />
              </Box>
            ) : (
              <div {...getRootProps()}>
                <input
                  id={inputId}
                  {...rest}
                  {...getInputProps()}
                  data-testid={`${testId}-input`}
                />

                <div
                  className={classNames(
                    classes.dropzone,
                    isInputHasFiles && classes.dropzoneMini,
                    !isDisabled && classes.active,
                    isDisabled && classes.disabled,
                    (error || dropError) && classes.error,
                  )}
                >
                  <Box mb="4" fontSize={32} height={32}>
                    <Icon name="vkhrtek_file" size="32" />
                  </Box>
                  <Box mb={{ xs: '0', md: '8' }}>
                    <Typography
                      display="block"
                      variant="caption"
                      color={
                        isDesktop
                          ? 'text.light.tertirary'
                          : 'original.brand.primary'
                      }
                      align="center"
                    >
                      {isInputHasFiles ? dropzoneText : description}
                    </Typography>
                  </Box>
                  {isDesktop && (
                    <Button
                      variant="outlined"
                      size="small"
                      color="primary"
                      disabled={disabled}
                      data-testid={`${testId}-upload`}
                    >
                      Загрузить
                    </Button>
                  )}
                </div>
              </div>
            )}
          </Box>
        )}
      </Box>
      {(error || dropError) && !isFileLoading && (
        <FileError text={errorToDisplay} />
      )}
    </Box>
  );
};
